import { humanize } from '../../shared/labels';
import {
  BaselineLayer,
  BaselineStackStatus,
  type AccountBaselineView,
  type BaselineLayerType,
  type BaselineStackView,
} from '../../types/backend';
import { stackReason } from './deriveOnboardingGates';

/**
 * One baseline's state, rolled up from its stacks. The keys are StatusChip's.
 *
 * There is no Outdated and no Failed: the status read records neither, and a
 * refusal or a block is a halt waiting on a person, which Refused and Blocked
 * say with their reason.
 */
export const BaselineHealth = {
  IN_SYNC: 'IN_SYNC',
  APPLYING: 'APPLYING',
  MISSING: 'MISSING',
  REFUSED: 'REFUSED',
  BLOCKED: 'BLOCKED',
  WAITING_ON_DEPENDENCY: 'WAITING_ON_DEPENDENCY',
  OFF: 'OFF',
} as const;

export type BaselineHealthType = typeof BaselineHealth[keyof typeof BaselineHealth];

/**
 * The backend's blocked reasons that are a wait for another baseline rather
 * than a fault. The status read already reports such a stack as PENDING; this
 * says which PENDING is a wait. Not a generated enum on the backend, so named
 * once here.
 */
const WAITING_REASONS = new Set(['WAITING_FOR_BASELINE', 'WAITING_FOR_ACCESS_ROLES']);

const TITLES: Record<string, string> = {
  access: 'Access',
  signals: 'Signals',
  data: 'Data',
  finops: 'FinOps',
};

export interface BaselineSummary {
  name: string;
  title: string;
  layer: BaselineLayerType;
  enabled: boolean;
  health: BaselineHealthType;
  /** Regions where a stack of this baseline has converged. */
  regions: string[];
  /** Why it is halted or waiting, in the backend's own words. */
  reason?: string;
}

const isWaiting = (s: BaselineStackView) =>
  s.status === BaselineStackStatus.PENDING && WAITING_REASONS.has(s.blocked_reason ?? '');

/** Worst first: the first state any stack is in decides the baseline's. */
const ORDER: Array<[BaselineHealthType, (s: BaselineStackView) => boolean]> = [
  [BaselineHealth.REFUSED, (s) => s.status === BaselineStackStatus.REFUSED],
  [BaselineHealth.BLOCKED, (s) => s.status === BaselineStackStatus.BLOCKED],
  [BaselineHealth.WAITING_ON_DEPENDENCY, isWaiting],
  [BaselineHealth.APPLYING, (s) => s.status === BaselineStackStatus.APPLYING || s.status === BaselineStackStatus.PENDING],
  [BaselineHealth.MISSING, (s) => s.status === BaselineStackStatus.NOT_STARTED],
];

function rollUp(stacks: BaselineStackView[]): Pick<BaselineSummary, 'health' | 'reason'> {
  if (stacks.length === 0) return { health: BaselineHealth.MISSING };
  for (const [health, matches] of ORDER) {
    const first = stacks.find(matches);
    if (first) {
      const explained = health === BaselineHealth.REFUSED
        || health === BaselineHealth.BLOCKED
        || health === BaselineHealth.WAITING_ON_DEPENDENCY;
      return { health, reason: explained ? stackReason(first) : undefined };
    }
  }
  return { health: BaselineHealth.IN_SYNC };
}

/** Every baseline of one account, in the order the status read lists them. */
export function summarizeBaselines(account: AccountBaselineView): BaselineSummary[] {
  const stacks = (account.stacks ?? []).filter((s) => s.desired);
  return (account.baselines ?? []).map((b) => {
    const own = stacks.filter((s) => s.baseline === b.name);
    const optionalOff = b.layer === BaselineLayer.OPTIONAL && !b.enabled;
    const regions = [...new Set(
      own.filter((s) => s.status === BaselineStackStatus.CONVERGED).map((s) => s.region),
    )];
    return {
      name: b.name,
      title: TITLES[b.name] ?? humanize(b.name),
      layer: b.layer,
      enabled: b.enabled,
      regions,
      ...(optionalOff ? { health: BaselineHealth.OFF } : rollUp(own)),
    };
  });
}
